// src/components/common/ToastMessage.tsx
import React, { useEffect, useState } from 'react';
import { styled, keyframes, css } from '@mui/material/styles';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { colors } from '../../theme/theme';

/* ----------------------------------------------------------------------------
 * Types & timing defaults
 * ------------------------------------------------------------------------- */

type ToastType = 'success' | 'error';

interface ToastMessageProps {
  show: boolean;
  type: ToastType;
  message?: string;
  onClose: () => void;
  disableAutoClose?: boolean;

  // Desktop timings (no ghost or slide-out on desktop)
  scaleInDuration?: number;
  iconDrawDuration?: number;
  iconDrawDelay?: number;
  holdDuration?: number;
  fadeOutDuration?: number;

  // Mobile timings — fully independent of desktop
  mobileScaleInDuration?: number;
  mobileIconDrawDuration?: number;
  mobileIconDrawDelay?: number;
  mobileHoldDuration?: number;
  mobileFadeOutDuration?: number;
  mobileGhostDelay?: number; // when the ghost starts rising, from CB anim start
  mobileGhostDuration?: number; // how long the ghost float-up takes
  mobileSlideOutDelay?: number; // when the badge starts sliding right, from CB anim start
  mobileSlideOutDuration?: number; // how long the slide-right + fade takes
}

const DEFAULT_TIMING = {
  scaleInDuration: 0.5,
  iconDrawDuration: 0.5,
  iconDrawDelay: 0.4,
  holdDuration: 1.5,
  fadeOutDuration: 0.1,
};

// Mobile defaults. Slide-out starts exactly when scale-in ends by default, so
// the badge settles and then glides away. Tune mobileSlideOutDelay upward if
// you want a brief pause at rest before it goes.
const DEFAULT_MOBILE_TIMING = {
  mobileScaleInDuration: 0.9,
  mobileIconDrawDuration: 0.4,
  mobileIconDrawDelay: 0.15,
  mobileHoldDuration: 3.4,
  mobileFadeOutDuration: 0.3,
  mobileGhostDelay: 0.7,
  mobileGhostDuration: 1.0,
  mobileSlideOutDelay: 2.2, // mobileScaleInDuration (0.9) + 0.4s pause at rest
  mobileSlideOutDuration: 0.6,
};

// Badge resting scale on mobile. Used by keyframes that need to preserve the
// badge's final size during follow-on transforms. Keep in sync with the 100%
// stop of mobileScaleInAnimation AND the 0% of mobileSlideOutRight.
const MOBILE_BADGE_RESTING_SCALE = 2;

/* ----------------------------------------------------------------------------
 * Keyframes
 * ------------------------------------------------------------------------- */

const scaleInAnimation = keyframes`
  0%   { transform: scale(0);   opacity: 0; }
  50%  { transform: scale(3.5); }
  65%  { transform: scale(2.8); }
  80%  { transform: scale(3.1); }
  100% { transform: scale(3);   opacity: 1; }
`;

const mobileScaleInAnimation = keyframes`
  0%   { transform: scale(0);   opacity: 0; }
  40%  { transform: scale(2.9); opacity: 1; }
  70%  { transform: scale(1.6); }
  90%  { transform: scale(2); }
  100% { transform: scale(2); }
`;

/*
 * Badge slide-out (mobile only). Runs as a second animation on CircleBadge
 * after scale-in finishes. Two things worth knowing:
 *
 * 1. The 0% keyframe matches mobileScaleInAnimation's 100% (scale(2) + no
 *    translate), so when this animation takes over there's no visual jump.
 *    The second animation in a comma-separated list wins for any shared
 *    property, so once this starts it fully controls transform + opacity.
 *
 * 2. Opacity hits 0 at 70% of the animation, while translate keeps going to
 *    100% — so the badge finishes fading before it finishes moving. That's
 *    the "fades out before it stops" behavior.
 */
const mobileSlideOutRight = keyframes`
  0%   { transform: scale(2) translateX(0);     opacity: 1; }
  70%  { transform: scale(2) translateX(70px);  opacity: 0; }
  100% { transform: scale(2) translateX(100px); opacity: 0; }
`;

const iconDrawAnimation = keyframes`
  0%   { stroke-dashoffset: 48; }
  100% { stroke-dashoffset: 0; }
`;

const fadeOutAnimation = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

const textFadeInAnimation = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const mobilePopIn = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.6);  opacity: 0; }
  60%  { transform: translate(-50%, -50%) scale(1.15); }
  80%  { transform: translate(-50%, -50%) scale(0.95); }
  100% { transform: translate(-50%, -50%) scale(1);    opacity: 1; }
`;

/*
 * Ghost lives in its own DOM element (not a pseudo-element of the badge), so
 * it no longer inherits the badge's transform. Values here are absolute — they
 * bake in the badge's resting scale of 2 so the ghost starts at the same
 * visual size as the settled badge and rises 120px on screen (equivalent to
 * the old "60px in local coords × parent scale of 2").
 */
const ghostFloatUp = keyframes`
  0%   { transform: scale(2);                      opacity: 0.2; }
  20%  {                                           opacity: 0.9; }
  65%  {                                           opacity: 0;   }
  100% { transform: translateY(-120px) scale(1.2); opacity: 0;   }
`;

/* ----------------------------------------------------------------------------
 * Styled components
 * ------------------------------------------------------------------------- */

/** Block the listed prop names from reaching the DOM. */
const notProps =
  (...names: string[]) =>
  (prop: PropertyKey) =>
    !names.includes(prop as string);

const ToastContainer = styled(Box, {
  shouldForwardProp: notProps(
    'fadeOut',
    'fadeOutDuration',
    'mobileFadeOutDuration'
  ),
})<{
  fadeOut: boolean;
  fadeOutDuration: number;
  mobileFadeOutDuration: number;
}>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 50px 32px 24px;
  border-radius: 8px;
  background: transparent;

  animation: ${(p) =>
    p.fadeOut
      ? css`
          ${fadeOutAnimation} ${p.fadeOutDuration}s ease-out forwards
        `
      : 'none'};

  @media (max-width: 600px) {
    top: 92%;
    left: 14%;
    animation: ${(p) =>
      p.fadeOut
        ? css`
            ${fadeOutAnimation} ${p.mobileFadeOutDuration}s ease-out forwards
          `
        : css`
            ${mobilePopIn} 0.4s ease-out forwards
          `};
  }
`;

/**
 * BadgeSlot: 30×30 positioning context that sits in ToastContainer's flex flow
 * where CircleBadge used to. Its only job is to hold the badge and (on mobile)
 * the ghost as absolutely-positioned siblings, so each can transform
 * independently. Dimensions match the old CircleBadge flex box exactly, so the
 * surrounding layout is unchanged.
 *
 * Note: BadgeSlot reserves its 30×30 footprint even after the badge slides
 * away. That's fine — ToastContainer is position:fixed so the empty slot
 * doesn't shift any page layout, and the text below keeps its gap.
 */
const BadgeSlot = styled(Box)`
  position: relative;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
`;

/**
 * Ghost: sibling of CircleBadge, not a pseudo-element. Because it's its own
 * element, CircleBadge's transform animations don't cascade into it —
 * meaning the badge can slide right while the ghost rises straight up.
 * DOM order (ghost before badge) puts it behind naturally, no z-index tricks.
 */
const Ghost = styled(Box, {
  shouldForwardProp: notProps(
    'toastType',
    'mobileGhostDelay',
    'mobileGhostDuration'
  ),
})<{
  toastType: ToastType;
  mobileGhostDelay: number;
  mobileGhostDuration: number;
}>`
  display: none;

  @media (max-width: 600px) {
    display: block;
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${(p) =>
      p.toastType === 'success' ? colors.success.main : '#d06079'};
    border: 2px solid #fff;
    opacity: 0;
    pointer-events: none;
    transform-origin: center center;
    animation: ${(p) => css`
      ${ghostFloatUp} ${p.mobileGhostDuration}s ease-out ${p.mobileGhostDelay}s forwards
    `};
  }
`;

/**
 * CircleBadge (mobile): runs two animations in sequence on the same element.
 *
 *   animation: scaleIn <dur>s ease-out forwards,
 *              slideOut <dur>s ease-in <delay>s forwards;
 *
 * Both animate `transform`. CSS resolves this by letting the later-declared
 * animation override during any overlap — and since slideOut's 0% matches
 * scaleIn's 100% (both are scale(2) at translateX 0), there's no visual jump
 * at the handoff. The `forwards` fill on scaleIn holds the final state during
 * slideOut's pre-delay; the `forwards` fill on slideOut holds the badge at
 * translateX(100px), opacity 0 after.
 *
 * This is what makes the badge independent of the ghost now: they're separate
 * DOM nodes, each with their own self-contained animation.
 */
const CircleBadge = styled(Box, {
  shouldForwardProp: notProps(
    'toastType',
    'scaleInDuration',
    'mobileScaleInDuration',
    'mobileSlideOutDelay',
    'mobileSlideOutDuration'
  ),
})<{
  toastType: ToastType;
  scaleInDuration: number;
  mobileScaleInDuration: number;
  mobileSlideOutDelay: number;
  mobileSlideOutDuration: number;
}>`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: ${(p) =>
    p.toastType === 'success' ? colors.success.main : '#d06079'};
  border: 2px solid #fff;
  box-shadow: ${colors.shadows.powerBold};
  display: flex;
  align-items: center;
  justify-content: center;
  transform-origin: center center;

  animation: ${scaleInAnimation} ${(p) => p.scaleInDuration}s ease-out forwards;

  @media (max-width: 600px) {
    animation:
      ${mobileScaleInAnimation} ${(p) => p.mobileScaleInDuration}s ease-out
        forwards,
      ${mobileSlideOutRight} ${(p) => p.mobileSlideOutDuration}s ease-in
        ${(p) => p.mobileSlideOutDelay}s forwards;
  }
`;

const CheckmarkSVG = styled('svg', {
  shouldForwardProp: notProps(
    'toastType',
    'iconDrawDuration',
    'iconDrawDelay',
    'mobileIconDrawDuration',
    'mobileIconDrawDelay'
  ),
})<{
  toastType: ToastType;
  iconDrawDuration: number;
  iconDrawDelay: number;
  mobileIconDrawDuration: number;
  mobileIconDrawDelay: number;
}>`
  width: 32px;
  height: 32px;
  display: block;
  flex-shrink: 0;

  .checkmark__check,
  .checkmark__cross {
    fill: none;
    stroke: #fff;
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 48;
    stroke-dashoffset: 48;
    animation: ${(p) => css`
      ${iconDrawAnimation} ${p.iconDrawDuration}s ease-out ${p.iconDrawDelay}s forwards
    `};
  }

  .checkmark__check {
    display: ${(p) => (p.toastType === 'success' ? 'block' : 'none')};
  }
  .checkmark__cross {
    display: ${(p) => (p.toastType === 'error' ? 'block' : 'none')};
  }

  @media (max-width: 600px) {
    .checkmark__check,
    .checkmark__cross {
      animation: ${(p) => css`
        ${iconDrawAnimation} ${p.mobileIconDrawDuration}s ease-out
          ${p.mobileIconDrawDelay}s forwards
      `};
    }
  }
`;

const ToastText = styled(Typography, {
  shouldForwardProp: notProps('toastType', 'textDelay', 'mobileTextDelay'),
})<{
  toastType: ToastType;
  textDelay: number;
  mobileTextDelay: number;
}>`
  color: ${(p) =>
    p.toastType === 'success' ? colors.success.main : '#d06079'};
  font-weight: 700;
  font-size: 2rem;
  text-align: center;
  max-width: 300px;
  padding: 0 16px;
  opacity: 0;

  animation: ${(p) => css`
    ${textFadeInAnimation} 0.3s ease-out ${p.textDelay}s forwards
  `};

  @media (max-width: 600px) {
    animation: ${(p) => css`
      ${textFadeInAnimation} 0.3s ease-out ${p.mobileTextDelay}s forwards
    `};
  }
`;

/* ----------------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------------- */

const ToastMessage: React.FC<ToastMessageProps> = ({
  show,
  type,
  message,
  onClose,
  disableAutoClose = false,

  scaleInDuration = DEFAULT_TIMING.scaleInDuration,
  iconDrawDuration = DEFAULT_TIMING.iconDrawDuration,
  iconDrawDelay = DEFAULT_TIMING.iconDrawDelay,
  holdDuration = DEFAULT_TIMING.holdDuration,
  fadeOutDuration = DEFAULT_TIMING.fadeOutDuration,

  mobileScaleInDuration = DEFAULT_MOBILE_TIMING.mobileScaleInDuration,
  mobileIconDrawDuration = DEFAULT_MOBILE_TIMING.mobileIconDrawDuration,
  mobileIconDrawDelay = DEFAULT_MOBILE_TIMING.mobileIconDrawDelay,
  mobileHoldDuration = DEFAULT_MOBILE_TIMING.mobileHoldDuration,
  mobileFadeOutDuration = DEFAULT_MOBILE_TIMING.mobileFadeOutDuration,
  mobileGhostDelay = DEFAULT_MOBILE_TIMING.mobileGhostDelay,
  mobileGhostDuration = DEFAULT_MOBILE_TIMING.mobileGhostDuration,
  mobileSlideOutDelay = DEFAULT_MOBILE_TIMING.mobileSlideOutDelay,
  mobileSlideOutDuration = DEFAULT_MOBILE_TIMING.mobileSlideOutDuration,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  const isMobile = useMediaQuery('(max-width:600px)', { noSsr: true });

  // Text fades in after the icon finishes drawing; one value per viewport.
  const textDelay = iconDrawDelay + iconDrawDuration;
  const mobileTextDelay = mobileIconDrawDelay + mobileIconDrawDuration;

  useEffect(() => {
    if (!show) return;

    setIsVisible(true);
    setIsFadingOut(false);

    if (disableAutoClose) return;

    // Resolve the active timing set for the current viewport so the JS
    // auto-close timer lines up with whichever CSS animations are playing.
    const active = isMobile
      ? {
          scaleIn: mobileScaleInDuration,
          iconEnd: mobileIconDrawDelay + mobileIconDrawDuration,
          ghostEnd: mobileGhostDelay + mobileGhostDuration,
          slideEnd: mobileSlideOutDelay + mobileSlideOutDuration,
          hold: mobileHoldDuration,
          fadeOut: mobileFadeOutDuration,
        }
      : {
          scaleIn: scaleInDuration,
          iconEnd: iconDrawDelay + iconDrawDuration,
          ghostEnd: 0, // no ghost on desktop
          slideEnd: 0, // no slide-out on desktop
          hold: holdDuration,
          fadeOut: fadeOutDuration,
        };

    // Hold starts once every in-animation has ended (scale-in, icon, ghost,
    // badge slide-out) so none of them get cut off mid-frame. The text
    // is still visible during the badge slide-out — only the badge moves —
    // so the container's fade-out shouldn't start until everything is done.
    const animationEndSec = Math.max(
      active.scaleIn,
      active.iconEnd,
      active.ghostEnd,
      active.slideEnd
    );
    const holdUntilFadeMs = (animationEndSec + active.hold) * 1000;

    let cleanupTimer: ReturnType<typeof setTimeout>;
    const fadeTimer = setTimeout(() => {
      setIsFadingOut(true);
      cleanupTimer = setTimeout(() => {
        setIsVisible(false);
        setIsFadingOut(false);
        onClose();
      }, active.fadeOut * 1000);
    }, holdUntilFadeMs);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(cleanupTimer);
    };
  }, [
    show,
    onClose,
    disableAutoClose,
    isMobile,
    scaleInDuration,
    iconDrawDuration,
    iconDrawDelay,
    holdDuration,
    fadeOutDuration,
    mobileScaleInDuration,
    mobileIconDrawDuration,
    mobileIconDrawDelay,
    mobileHoldDuration,
    mobileFadeOutDuration,
    mobileGhostDelay,
    mobileGhostDuration,
    mobileSlideOutDelay,
    mobileSlideOutDuration,
  ]);

  if (!isVisible) return null;

  return (
    <ToastContainer
      fadeOut={isFadingOut}
      fadeOutDuration={fadeOutDuration}
      mobileFadeOutDuration={mobileFadeOutDuration}
    >
      <BadgeSlot>
        <Ghost
          toastType={type}
          mobileGhostDelay={mobileGhostDelay}
          mobileGhostDuration={mobileGhostDuration}
        />
        <CircleBadge
          toastType={type}
          scaleInDuration={scaleInDuration}
          mobileScaleInDuration={mobileScaleInDuration}
          mobileSlideOutDelay={mobileSlideOutDelay}
          mobileSlideOutDuration={mobileSlideOutDuration}
        >
          <CheckmarkSVG
            toastType={type}
            iconDrawDuration={iconDrawDuration}
            iconDrawDelay={iconDrawDelay}
            mobileIconDrawDuration={mobileIconDrawDuration}
            mobileIconDrawDelay={mobileIconDrawDelay}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 52 52"
          >
            <path
              className="checkmark__check"
              d="M14.1 27.2l7.1 7.2 16.7-16.8"
            />
            <path
              className="checkmark__cross"
              d="M 16,16 L 36,36 M 36,16 L 16,36"
            />
          </CheckmarkSVG>
        </CircleBadge>
      </BadgeSlot>

      {message && (
        <ToastText
          toastType={type}
          textDelay={textDelay}
          mobileTextDelay={mobileTextDelay}
        >
          {message}
        </ToastText>
      )}
    </ToastContainer>
  );
};

export default ToastMessage;
export type { ToastType, ToastMessageProps };
